import { ShoppingBag, Users, Bike, DollarSign, Clock, AlertCircle, Store, TrendingUp } from 'lucide-react';
import { StatCard, Card } from '@/components/ui';
import { useTranslation } from '@/hooks/useTranslation';
import { mockStats, mockOrders } from '@/services/mockData';

export default function Dashboard() {
  const { t, formatCurrency, language } = useTranslation();

  const dashboardStats = mockStats;
  const orders = mockOrders.slice(0, 5);

  const statusColors: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    preparing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    on_the_way: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  const statusLabels: Record<string, string> = {
    pending: t('pending'),
    confirmed: t('confirmed'),
    preparing: t('preparing'),
    on_the_way: t('onTheWay'),
    delivered: t('delivered'),
    cancelled: t('cancelled'),
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('dashboard')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {language === 'ar' ? 'مرحباً بك في لوحة التحكم' : 'Welcome to the dashboard'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('todayOrders')}
          value={dashboardStats.today_orders}
          icon={<ShoppingBag size={24} />}
          color="primary"
          trend={{ value: 12, isUp: true }}
        />
        <StatCard
          title={t('totalRevenue')}
          value={formatCurrency(dashboardStats.month_revenue)}
          icon={<DollarSign size={24} />}
          color="success"
          trend={{ value: 8, isUp: true }}
        />
        <StatCard
          title={t('totalUsers')}
          value={dashboardStats.total_users.toLocaleString()}
          icon={<Users size={24} />}
          color="info"
          trend={{ value: 5, isUp: true }}
        />
        <StatCard
          title={t('totalDrivers')}
          value={dashboardStats.total_drivers}
          icon={<Bike size={24} />}
          color="warning"
        />
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <Store size={20} className="mx-auto text-purple-500 mb-1" />
          <p className="text-xl font-bold text-slate-800 dark:text-white">{dashboardStats.total_cafes}</p>
          <p className="text-xs text-slate-500">{t('cafes')}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <TrendingUp size={20} className="mx-auto text-emerald-500 mb-1" />
          <p className="text-xl font-bold text-slate-800 dark:text-white">{dashboardStats.active_cafes}</p>
          <p className="text-xs text-slate-500">{language === 'ar' ? 'مقهى نشط' : 'Active Cafes'}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <Clock size={20} className="mx-auto text-blue-500 mb-1" />
          <p className="text-xl font-bold text-slate-800 dark:text-white">{dashboardStats.avg_delivery_time} {language === 'ar' ? 'د' : 'min'}</p>
          <p className="text-xs text-slate-500">{language === 'ar' ? 'متوسط التوصيل' : 'Avg Delivery'}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <DollarSign size={20} className="mx-auto text-amber-500 mb-1" />
          <p className="text-xl font-bold text-slate-800 dark:text-white">{formatCurrency(dashboardStats.total_revenue)}</p>
          <p className="text-xs text-slate-500">{language === 'ar' ? 'إيراد اليوم' : "Today's Revenue"}</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <Card title={t('recentOrders')} className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700">
                  <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {t('orderNumber')}
                  </th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {t('customer')}
                  </th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {t('cafe')}
                  </th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {t('amount')}
                  </th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {t('status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order: any) => (
                  <tr key={order.id} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <td className="p-4">
                      <span className="font-semibold text-slate-800 dark:text-slate-200">
                        {order.order_number}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">{order.customer}</td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">{order.cafe}</td>
                    <td className="p-4 font-semibold text-slate-800 dark:text-slate-200">
                      {formatCurrency(order.amount)}
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-lg text-sm font-semibold ${statusColors[order.status]}`}>
                        {statusLabels[order.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pending Actions */}
        <Card title={t('pendingActions')}>
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="text-amber-600 dark:text-amber-400" size={20} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {language === 'ar' ? `${dashboardStats.pending_orders} طلبات معلقة` : `${dashboardStats.pending_orders} Pending Orders`}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'ar' ? 'بحاجة للمراجعة' : 'Needs review'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Bike className="text-blue-600 dark:text-blue-400" size={20} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {language === 'ar' ? '3 سائقين جدد' : '3 New Drivers'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'ar' ? 'في انتظار الموافقة' : 'Awaiting approval'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {language === 'ar' ? '2 شكاوى' : '2 Complaints'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'ar' ? 'تحتاج للرد' : 'Needs response'}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
